import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Play, ChevronRight, ClipboardEdit, Loader2, Sparkles, RefreshCw, Activity, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MyTrainingPlan = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('session_token') || localStorage.getItem('token');

  const [loading, setLoading] = useState(true);
  const [routineData, setRoutineData] = useState(null);
  const [acting, setActing] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  const fetchRoutine = async () => {
    setLoading(true);
    try {
      const [routineRes, meRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/workout-routine`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${BACKEND_URL}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => null)
      ]);
      setRoutineData(routineRes.data);
      setIsAdmin(meRes?.data?.role === 'admin');
    } catch (err) {
      // no routine
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRoutine(); }, []);

  const handleRegenerate = async () => {
    const ok = window.confirm('¿Regenerar tu rutina actual? Se reemplazará con una nueva.');
    if (!ok) return;
    setActing(true);
    try {
      await axios.post(`${BACKEND_URL}/api/workout-routine/generate`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Rutina regenerada');
      await fetchRoutine();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo regenerar');
    } finally {
      setActing(false);
    }
  };

  const handleIncreaseIntensity = async () => {
    setActing(true);
    try {
      await axios.post(`${BACKEND_URL}/api/workout-routine/increase-intensity`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Intensidad actualizada');
      await fetchRoutine();
    } catch (err) {
      toast.error('No se pudo actualizar');
    } finally {
      setActing(false);
    }
  };

  const handleNewCycle = async () => {
    setActing(true);
    try {
      await axios.post(`${BACKEND_URL}/api/workout-routine/new-cycle`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Nueva rutina lista');
      await fetchRoutine();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo crear la nueva rutina');
    } finally {
      setActing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-pink-500 animate-spin" />
      </div>
    );
  }

  const routine = routineData?.routine;
  const hasRoutine = !!routine;

  return (
    <div className="min-h-screen bg-[#09090b] py-10 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-2xl bg-pink-500/15 border border-pink-500/30 flex items-center justify-center">
            <Play className="w-6 h-6 text-pink-400" />
          </div>
          <h1 className="font-barlow text-3xl md:text-4xl font-black text-white uppercase tracking-tight">
            Mi Plan de <span className="text-pink-500">Entrenamiento</span>
          </h1>
        </div>

        {!hasRoutine ? (
          // Estado sin rutina: mostrar mensaje suave para llenar formulario
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-3xl p-10 text-center" data-testid="no-routine-state">
            <div className="w-16 h-16 mx-auto mb-5 rounded-2xl bg-pink-500/10 border border-pink-500/20 flex items-center justify-center">
              <ClipboardEdit className="w-8 h-8 text-pink-400" />
            </div>
            <h2 className="font-barlow text-2xl md:text-3xl font-black text-white uppercase mb-3">Recuerda llenar el formulario</h2>
            <p className="text-zinc-400 mb-8 max-w-md mx-auto">Completa el formulario para obtener tu rutina de entrenamiento personalizada.</p>
            <Button onClick={() => navigate('/training-plan/form')} className="bg-pink-600 hover:bg-pink-500 rounded-full px-8 py-6 font-bold uppercase tracking-wider" data-testid="open-form-btn">
              Comenzar formulario
            </Button>
          </div>
        ) : (
          <>
            {/* Descargar rutina */}
            <div className="text-right mb-4 no-print">
              <Button
                onClick={() => window.print()}
                className="bg-pink-600 hover:bg-pink-500 text-white rounded-full px-6"
                data-testid="training-plan-download-btn"
              >
                📥 Descargar Rutina (PDF)
              </Button>
            </div>

            {/* Banners */}
            {routineData.show_new_cycle_banner && (
              <div className="mb-6 bg-gradient-to-r from-pink-600/20 to-purple-600/20 border-2 border-pink-500/50 rounded-3xl p-6 no-print" data-testid="new-cycle-banner">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-pink-500/20 flex items-center justify-center flex-shrink-0">
                    <RefreshCw className="w-6 h-6 text-pink-400" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-white font-bold text-lg mb-1">¡Es momento de una rutina nueva!</h3>
                    <p className="text-zinc-300 text-sm mb-4">Llevas 16 semanas con esta rutina. Vamos a actualizarla manteniendo el progreso que ya tienes.</p>
                    <Button onClick={handleNewCycle} disabled={acting} className="bg-pink-600 hover:bg-pink-500 rounded-full px-6 font-bold uppercase">
                      {acting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Preparando...</> : 'Crear rutina nueva'}
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {!routineData.show_new_cycle_banner && routineData.show_intensity_banner && (
              <div className="mb-6 bg-gradient-to-r from-amber-600/15 to-pink-600/15 border-2 border-amber-500/40 rounded-3xl p-6 no-print" data-testid="intensity-banner">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-6 h-6 text-amber-400" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-white font-bold text-lg mb-1">¡Genial, llevas {routineData.weeks_active} semanas con esta rutina!</h3>
                    <p className="text-zinc-300 text-sm mb-4">Es hora de subir el nivel. Vamos a aumentar la intensidad de tu rutina actual.</p>
                    <Button onClick={handleIncreaseIntensity} disabled={acting} className="bg-amber-500 hover:bg-amber-400 text-zinc-900 rounded-full px-6 font-bold uppercase">
                      {acting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Actualizando...</> : 'Aumentar intensidad'}
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Lista de días */}
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-3xl p-6 mb-6 no-print">
              <div className="space-y-3" data-testid="routine-days">
                {(() => {
                  // Calcular semana actual
                  const created = new Date(routine.created_at);
                  const weeksSinceStart = Math.floor((new Date() - created) / (7 * 24 * 60 * 60 * 1000));
                  const currentWeek = weeksSinceStart + 1;
                  const completedThisWeek = (routine.days_completed || []).filter(c => c.week_number === currentWeek);
                  return (
                    <>
                      <div className="flex items-center justify-between mb-2 px-1">
                        <span className="text-xs text-zinc-500 uppercase tracking-wider font-bold">Semana {currentWeek}</span>
                        <span className="text-xs text-emerald-400 font-medium">{completedThisWeek.length}/{routine.days?.length || 0} completados</span>
                      </div>
                      {routine.days?.map(day => {
                        const isCompleted = completedThisWeek.some(c => c.day_number === day.day_number);
                        return (
                          <Link
                            key={day.day_number}
                            to={`/training-plan/day/${day.day_number}`}
                            className="block group"
                            data-testid={`routine-day-${day.day_number}`}
                          >
                            <div className={`flex items-center justify-between p-4 border rounded-2xl transition-all ${isCompleted ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-zinc-800/50 hover:bg-zinc-800 border-zinc-700/50 hover:border-pink-500/50'}`}>
                              <div className="flex items-center gap-4">
                                <div className={`w-12 h-12 rounded-xl flex items-center justify-center font-barlow font-bold text-lg ${isCompleted ? 'bg-emerald-500 text-white' : 'bg-pink-500 text-white'}`}>
                                  {isCompleted ? <Check className="w-5 h-5" /> : day.day_number}
                                </div>
                                <div>
                                  <span className={`font-medium ${isCompleted ? 'text-emerald-200' : 'text-white'}`}>Día {day.day_number}</span>
                                  <p className={`text-sm ${isCompleted ? 'text-emerald-300/70' : 'text-zinc-500'}`}>{day.focus}</p>
                                </div>
                              </div>
                              <ChevronRight className="w-5 h-5 text-pink-500 group-hover:translate-x-1 transition-transform" />
                            </div>
                          </Link>
                        );
                      })}
                    </>
                  );
                })()}
              </div>
            </div>

            {/* Vista imprimible de la rutina completa: todos los días con sus ejercicios y series (solo visible al imprimir) */}
            <div className="print-only space-y-6 mb-6">
              {routine.days?.map(day => (
                <div key={day.day_number} className="mb-6">
                  <h3 className="text-lg font-bold mb-2">Día {day.day_number} – {day.focus}</h3>
                  <div className="space-y-3">
                    {(day.exercises || []).map((ex, idx) => (
                      <div key={`${ex.exercise_id}-${idx}`} className="mb-3">
                        <div className="font-semibold">{ex.name}{ex.muscle_group ? ` (${ex.muscle_group.replace('_', ' ')})` : ''}</div>
                        {Array.isArray(ex.series) && ex.series.length > 0 && (
                          <ul className="ml-4 text-sm list-disc">
                            {ex.series.map((s, sIdx) => (
                              <li key={sIdx}>{s}</li>
                            ))}
                          </ul>
                        )}
                        {ex.technique?.name && (
                          <div className="ml-4 text-sm italic">
                            {ex.technique.name}{ex.technique.description ? `: ${ex.technique.description}` : ''}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Cardio recomendado */}
            {routine.cardio && (
              <div className="bg-zinc-900/50 border border-zinc-800 rounded-3xl p-6 mb-6" data-testid="cardio-section">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-emerald-500/15 flex items-center justify-center">
                    <Activity className="w-5 h-5 text-emerald-400" />
                  </div>
                  <h2 className="text-lg font-bold text-white">Cardio recomendado</h2>
                </div>
                <div className="bg-zinc-800/50 rounded-2xl p-5">
                  <div className="flex items-baseline gap-2 mb-2">
                    <span className="font-barlow text-3xl font-black text-emerald-400">{routine.cardio.sessions_per_week}</span>
                    <span className="text-zinc-400 text-sm">sesiones por semana</span>
                  </div>
                  <p className="text-zinc-300 text-sm font-medium mb-2">{routine.cardio.type}</p>
                  <p className="text-zinc-500 text-xs">{routine.cardio.notes}</p>
                </div>

                {/* Explicación LISS/HIIT: contenido estático e igual para todas las usuarias; las cantidades de arriba siguen siendo calculadas por el sistema */}
                <div className="mt-4 bg-emerald-500/10 border border-emerald-500/30 rounded-2xl p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-[10px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/40">LISS</span>
                    <span className="text-white font-bold">Cardio de baja intensidad</span>
                  </div>
                  <p className="text-zinc-300 text-sm mb-3">Es cardio constante a ritmo moderado donde puedes mantener una conversación. Quemas grasa sin afectar tu masa muscular. Ideal después del entrenamiento o en días de descanso.</p>
                  <p className="text-emerald-400 text-[11px] font-bold uppercase tracking-wider mb-1">Ejemplos:</p>
                  <ul className="text-zinc-300 text-sm space-y-1 list-disc list-inside">
                    <li>Caminar inclinada en la trotadora (10% inclinación) 30-45 min</li>
                    <li>Bicicleta estática a ritmo moderado 30-45 min</li>
                    <li>Elíptica suave 30-45 min</li>
                    <li>Caminar al aire libre a buen paso 40-60 min</li>
                  </ul>
                </div>

                <div className="mt-4 bg-pink-500/10 border border-pink-500/30 rounded-2xl p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-[10px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-full bg-pink-500/20 text-pink-300 border border-pink-500/40">HIIT</span>
                    <span className="text-white font-bold">Cardio por intervalos de alta intensidad</span>
                  </div>
                  <p className="text-zinc-300 text-sm mb-3">Alternas explosiones de máximo esfuerzo con periodos de recuperación. Sesiones cortas (15-25 min) muy efectivas para quemar grasa y mejorar capacidad cardiovascular.</p>
                  <p className="text-pink-400 text-[11px] font-bold uppercase tracking-wider mb-1">Ejemplos para hacer HIIT (elige uno):</p>
                  <ul className="text-zinc-300 text-sm space-y-1 list-disc list-inside">
                    <li><span className="font-semibold text-white">1 min a máxima velocidad</span> + 1:30 min descanso · repite 8 rondas</li>
                    <li><span className="font-semibold text-white">30 segundos sprint</span> + 1 min caminata · repite 10-12 rondas</li>
                    <li><span className="font-semibold text-white">40 segundos burpees/jumping jacks</span> + 20 segundos descanso · 8 rondas</li>
                    <li><span className="font-semibold text-white">45 segundos bicicleta fuerte</span> + 15 segundos descanso · 10 rondas</li>
                    <li><span className="font-semibold text-white">20 segundos máximo</span> + 10 segundos descanso (Tabata) · 8 rondas = 4 min · puedes hacer 2-3 bloques</li>
                  </ul>
                </div>
              </div>
            )}

            {/* Editar formulario */}
            <div className="text-center space-y-3 no-print">
              {isAdmin && (
                <div>
                  <Button
                    onClick={handleRegenerate}
                    disabled={acting}
                    className="bg-zinc-800 hover:bg-zinc-700 border border-pink-500/30 text-pink-300 rounded-full px-6 font-bold uppercase text-xs tracking-wider"
                    data-testid="admin-regenerate-routine-btn"
                  >
                    {acting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Regenerando...</> : <><RefreshCw className="w-4 h-4 mr-2" /> Regenerar rutina (admin)</>}
                  </Button>
                </div>
              )}
              <button
                onClick={() => navigate('/training-plan/form')}
                className="text-zinc-500 hover:text-pink-400 text-sm underline underline-offset-4"
                data-testid="edit-form-link"
              >
                Editar mi formulario
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MyTrainingPlan;
